import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Feedback.css";
import axios from "axios";
import LoginModal from "../components/LoginModal";

const Feedback = () => {
  const [account, setAccount] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [formData, setFormData] = useState({
    isDeveloper: "",
    yearsExperience: "",
    currentField: "",
    usedOtherPlatforms: "",
    homepageClarity: "",
    homepageButtons: "",
    nextSteps: "",
    accessibility: "",
    homepageSuggestions: "",
    problemEase: "",
    visualClarity: "",
    blockUnderstanding: "",
    featureComparison: "",
    problemSuggestions: "",
    featuresToAdd: "",
    changesToWebsite: "",
    additionalComments: "",
    likelihoodToUse: 5,
    overallExperience: 5,
  });
  const navigate = useNavigate();

  // Fetch logged-in user details
  const fetchUserDetails = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setAccount(null);
      return;
    }

    try {
      const config = {
        withCredentials: true,
        headers: {},
      };

      if (token && token !== "cookie") {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/users/account/${userId}`,
        config
      );
      setAccount(res.data);
    } catch (err) {
      console.error("Error fetching user:", err);
      setAccount(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) setShowLoginModal(true);
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Called after successful login from LoginModal
  // Called after successful login from LoginModal
  const handleLoginSuccess = async ({ token, userId, role } = {}) => {
    const storedToken = token || "cookie"; // Sentinel for cookie-based auth
    if (storedToken) localStorage.setItem("token", storedToken);
    if (userId) localStorage.setItem("userId", String(userId));
    if (role) localStorage.setItem("role", role);

    // 🔔 Tell the rest of the app we've logged in (same-tab friendly)
    window.dispatchEvent(
      new CustomEvent("auth:login", {
        detail: { token: storedToken, userId, role },
      })
    );

    setShowLoginModal(false);
    await fetchUserDetails();
  };

  // Listen for login/logout events from other tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token") {
        if (e.newValue) {
          handleLoginSuccess();
        } else {
          setAccount(null);
          setShowLoginModal(true);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((fd) => ({ ...fd, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to submit feedback.");
      setShowLoginModal(true);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/feedback`,
        {
          username: account?.username || "Unknown",
          email: account?.email || "Unknown",
          responses: formData,
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        alert("Feedback submitted successfully!");
        navigate("/problems");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("Something went wrong. Please try again later.");
    }
  };

  return (
    <div className="feedback-page">
      {/* Show the login modal as an overlay if not logged in */}
      {showLoginModal && (
        <LoginModal
          onLogin={handleLoginSuccess}
          onClose={() => setShowLoginModal(false)}
        />
      )}

      <div className="survey-container" aria-hidden={showLoginModal}>
        <h1 className="survey-title">Blockcode Alpha Testing Feedback</h1>
        <form onSubmit={handleSubmit} className="survey-form">
          <div className="fb-form-group">
            <label htmlFor="username">Username:</label>
            <input
              type="text"
              id="username"
              value={account?.username || "Unknown"}
              readOnly
              className="readonly-input"
            />
          </div>

          <div className="fb-form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="text"
              id="email"
              value={account?.email || "Unknown"}
              readOnly
              className="readonly-input"
            />
          </div>

          {/* SECTION 1: User Background */}
          <h2>Section 1: User Background</h2>
          <div className="fb-form-group">
            <label>Are you a developer?</label>
            <select
              name="isDeveloper"
              value={formData.isDeveloper}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {formData.isDeveloper === "yes" && (
            <div className="fb-form-group">
              <label>How many years of experience do you have?</label>
              <select
                name="yearsExperience"
                value={formData.yearsExperience}
                onChange={handleChange}
                required
              >
                <option value="">-- Select --</option>
                <option value="0-1">0–1 years</option>
                <option value="1-3">1–3 years</option>
                <option value="3+">3+ years</option>
              </select>
            </div>
          )}

          {formData.isDeveloper === "no" && (
            <div className="form-group">
              <label>What field do you currently work in?</label>
              <input
                type="text"
                name="currentField"
                value={formData.currentField}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Have you used LeetCode or HackerRank before?</label>
            <select
              name="usedOtherPlatforms"
              value={formData.usedOtherPlatforms}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* SECTION 2: Homepage & Navigation */}
          <h2>Section 2: Homepage & Navigation</h2>
          <div className="form-group">
            <label>Did the homepage make it clear what Blockcode is?</label>
            <select
              name="homepageClarity"
              value={formData.homepageClarity}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>Was the purpose of the homepage buttons intuitive?</label>
            <select
              name="homepageButtons"
              value={formData.homepageButtons}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              Did you know what to do next after landing on the homepage?
            </label>
            <select
              name="nextSteps"
              value={formData.nextSteps}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              Was it easy to access key sections like Problems, Learning Path,
              and Import a Problem?
            </label>
            <select
              name="accessibility"
              value={formData.accessibility}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              If you could change or add something to this section, what would
              it be?
            </label>
            <textarea
              name="homepageSuggestions"
              value={formData.homepageSuggestions}
              onChange={handleChange}
            />
          </div>

          {/* SECTION 3: Problem Interface & Blocks */}
          <h2>Section 3: Problem Interface & Blocks</h2>
          <div className="form-group">
            <label>Were you able to solve problems easily?</label>
            <select
              name="problemEase"
              value={formData.problemEase}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>Were the blocks and problem layout visually clear?</label>
            <select
              name="visualClarity"
              value={formData.visualClarity}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              Did you understand how the blocks were intended to work?
            </label>
            <select
              name="blockUnderstanding"
              value={formData.blockUnderstanding}
              onChange={handleChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>
              How did this compare to similar platforms (like LeetCode,
              HackerRank)?
            </label>
            <textarea
              name="featureComparison"
              value={formData.featureComparison}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>
              If you could change or add something to this section, what would
              it be?
            </label>
            <textarea
              name="problemSuggestions"
              value={formData.problemSuggestions}
              onChange={handleChange}
            />
          </div>

          {/* SECTION 4: General Feedback */}
          <h2>Section 4: General Feedback</h2>
          <div className="form-group">
            <label>What features would you like to add?</label>
            <textarea
              name="featuresToAdd"
              value={formData.featuresToAdd}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>What would you change on the website?</label>
            <textarea
              name="changesToWebsite"
              value={formData.changesToWebsite}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Any additional feedback?</label>
            <textarea
              name="additionalComments"
              value={formData.additionalComments}
              onChange={handleChange}
            />
          </div>

          {/* SECTION 5: Likelihood & Rating */}
          <h2>Section 5: Likelihood & Rating</h2>
          <div className="form-group">
            <label>
              On a scale of 1–10, how likely are you to use Blockcode again?
            </label>
            <input
              type="range"
              name="likelihoodToUse"
              min="1"
              max="10"
              value={formData.likelihoodToUse}
              onChange={handleChange}
            />
            <p>{formData.likelihoodToUse} (1 = Never again, 10 = Absolutely)</p>
          </div>

          <div className="form-group">
            <label>How would you rate your overall experience?</label>
            <input
              type="range"
              name="overallExperience"
              min="1"
              max="10"
              value={formData.overallExperience}
              onChange={handleChange}
            />
            <p>{formData.overallExperience} (1 = Poor, 10 = Excellent)</p>
          </div>

          <button type="submit" className="submit-button">
            Submit Feedback
          </button>
        </form>
      </div>
    </div>
  );
};

export default Feedback;
